import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Descarga del historial de movimientos del cliente autenticado.
 *
 * El `usuarioId` NO se manda: el backend lo deriva del token de Cognito. No hay
 * forma de pedir el historial de otra persona, y tampoco hay que acordarse de
 * mandar nada.
 *
 * POR QUÉ ESTO NO ES UN `<a href>`. El endpoint exige el token en la cabecera
 * `Authorization`, y un link del navegador no manda cabeceras. Hay que pedirlo
 * con `HttpClient` —al que el interceptor le pega el token— y quedarse con el
 * blob. El efecto secundario es bueno: como controlamos el momento en que
 * arranca y el momento en que termina, el aviso lateral sale gratis.
 *
 * EL AVISO VIVE ACÁ Y NO EN EL COMPONENTE, para que la descarga se comporte
 * igual desde donde se la llame. Hoy la llama el sidebar; mañana puede llamarla
 * la pantalla de perfil.
 */
@Injectable({
  providedIn: 'root',
})
export class ReporteClienteService {
  private readonly url = `${environment.apiNest}reportes-cliente/movimientos`;

  /**
   * Si hay una descarga en curso.
   *
   * El sidebar ata el ítem a esto para deshabilitarlo mientras dura. Si no, el
   * segundo click gasta una de las descargas del día del cliente y le devuelve
   * un 429 por culpa nuestra.
   */
  private readonly descargandoSubject = new BehaviorSubject<boolean>(false);
  readonly descargando$ = this.descargandoSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly snackBar: MatSnackBar,
  ) {}

  get descargando(): boolean {
    return this.descargandoSubject.value;
  }

  /**
   * Pide el historial completo y lo baja.
   *
   * No recibe fechas: el reporte es el historial entero. El backend acepta un
   * rango opcional, pero el botón del sidebar pide todo, que es lo que el
   * cliente espera cuando dice "mi historial".
   */
  async descargar(): Promise<void> {
    if (this.descargando) return;

    this.descargandoSubject.next(true);
    // El aviso se abre DENTRO del try. Si se abriera afuera y fallara, no se
    // entraria al `finally` y la bandera quedaria en true para siempre: el item
    // del sidebar queda apagado hasta que el cliente recargue la pagina, sin
    // ninguna forma de salir de ahi.
    let aviso: MatSnackBarRef<unknown> | null = null;

    try {
      aviso = this.avisoEnCurso();
      const respuesta = await firstValueFrom(
        this.http.get(this.url, {
          responseType: 'blob',
          observe: 'response',
        }),
      );

      this.guardarArchivo(respuesta);
      aviso?.dismiss();
      this.avisoListo();
    } catch (error) {
      aviso?.dismiss();
      this.avisoError(await this.mensajeDeError(error));
    } finally {
      this.descargandoSubject.next(false);
    }
  }

  // -------------------------------------------------------------------------
  // El archivo
  // -------------------------------------------------------------------------

  /**
   * Toma el blob y dispara la descarga del navegador.
   *
   * El nombre sale de `Content-Disposition`, que el backend expone por CORS a
   * propósito. Si por lo que sea no llega, se arma uno con la fecha: mejor un
   * nombre nuestro que un `descarga.bin`.
   */
  private guardarArchivo(respuesta: HttpResponse<Blob>): void {
    const blob = respuesta.body;
    if (!blob) throw new Error('La respuesta llegó vacía.');

    const enlace = document.createElement('a');
    const url = URL.createObjectURL(blob);
    enlace.href = url;
    enlace.download = this.nombreDeArchivo(respuesta);
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);

    // Se revoca DESPUÉS, no en este mismo tick. Revocar la URL justo detrás
    // del `click()` llega a cancelar la descarga en algunos navegadores: el
    // click abre el guardado de forma asíncrona y se encuentra con que la URL
    // ya no existe. Sale un archivo de 0 bytes, o ninguno.
    //
    // Y revocarla hay que revocarla: si no, el blob queda retenido en memoria
    // hasta que se recargue la página, y un historial largo pesa varios MB.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  private nombreDeArchivo(respuesta: HttpResponse<Blob>): string {
    const cabecera = respuesta.headers.get('Content-Disposition') ?? '';
    const conComillas = /filename="([^"]+)"/i.exec(cabecera);
    if (conComillas?.[1]) return conComillas[1];
    const sinComillas = /filename=([^;]+)/i.exec(cabecera);
    if (sinComillas?.[1]) return sinComillas[1].trim();

    const hoy = new Date().toISOString().slice(0, 10);
    return `Movimientos-${hoy}.xlsx`;
  }

  // -------------------------------------------------------------------------
  // Errores
  // -------------------------------------------------------------------------

  /**
   * Saca el mensaje que escribió el backend.
   *
   * LA TRAMPA: como la petición pidió `responseType: 'blob'`, el cuerpo de una
   * respuesta de ERROR también llega como Blob, no como JSON. Si se lo lee
   * directo se obtiene `[object Blob]` y el cliente termina viendo "Error" en
   * vez de "termina tu verificación" o "ya descargaste tu historial 5 veces".
   * Hay que leer el blob como texto y recién ahí parsearlo.
   */
  private async mensajeDeError(error: unknown): Promise<string> {
    if (!(error instanceof HttpErrorResponse)) {
      return 'No se pudo generar tu historial. Intenta de nuevo.';
    }

    if (error.status === 0) {
      return 'Se interrumpió la conexión. Revisa tu internet e intenta de nuevo.';
    }

    const crudo = await this.cuerpoComoTexto(error);
    const mensaje = this.mensajeDelCuerpo(crudo);
    if (mensaje) return mensaje;

    if (error.status === 401) return 'Tu sesión expiró. Vuelve a iniciar sesión.';
    if (error.status === 403) {
      return 'Termina tu verificación de cuenta para poder descargar tu historial.';
    }
    if (error.status === 429) {
      return 'Ya descargaste tu historial varias veces hoy. Intenta más tarde.';
    }
    if (error.status === 503) {
      return 'La descarga no está disponible en este momento. Intenta más tarde.';
    }
    return 'No se pudo generar tu historial. Intenta de nuevo.';
  }

  private async cuerpoComoTexto(error: HttpErrorResponse): Promise<string> {
    const cuerpo = error.error;
    if (typeof cuerpo === 'string') return cuerpo;
    if (cuerpo instanceof Blob) {
      try {
        return await cuerpo.text();
      } catch {
        return '';
      }
    }
    if (cuerpo && typeof cuerpo === 'object') {
      try {
        return JSON.stringify(cuerpo);
      } catch {
        return '';
      }
    }
    return '';
  }

  /** El backend manda `{ message: '...' }`, a veces con `details`. */
  private mensajeDelCuerpo(crudo: string): string | null {
    if (!crudo.trim()) return null;
    try {
      const json = JSON.parse(crudo) as { message?: unknown };
      if (typeof json.message === 'string' && json.message.trim()) {
        return json.message;
      }
      if (Array.isArray(json.message) && typeof json.message[0] === 'string') {
        return json.message[0];
      }
    } catch {
      // No era JSON. No se muestra el crudo: puede ser un HTML de error del
      // balanceador y el cliente no tiene por qué leer eso.
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // Los tres avisos
  // -------------------------------------------------------------------------

  /**
   * Sin duración: se cierra cuando termina la descarga, no antes.
   *
   * Y sin porcentaje: el archivo se genera y se manda al mismo tiempo, así que
   * el tamaño total no se conoce al empezar. Una barra de progreso ahí sería
   * inventada.
   */
  private avisoEnCurso(): MatSnackBarRef<unknown> {
    return this.snackBar.open('Preparando tu historial…', '', {
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['aguacate-snackbar-progreso'],
    });
  }

  private avisoListo(): void {
    this.snackBar.open('Tu historial se descargó', 'Ok', {
      duration: 4000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['aguacate-snackbar'],
    });
  }

  private avisoError(mensaje: string): void {
    this.snackBar.open(mensaje, 'Cerrar', {
      // Más tiempo que el de éxito: éste hay que leerlo.
      duration: 8000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['aguacate-snackbar-error'],
    });
  }
}
